import React, { createContext, useContext, useState, useCallback } from 'react';

export const MODAL_IDS = {
    SECTION_EDITOR: 'section-editor',
    PAGE_EDITOR: 'page-editor',
    COLOR_PALETTE: 'color-palette',
    IMAGE_PICKER: 'image-picker',
    SITE_SETTINGS: 'site-settings',
    ADD_SECTION: 'add-section',
    VARIANT_SELECTOR: 'variant-selector'
};

const ModalContext = createContext();

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within ModalProvider');
    }
    return context;
};

export const ModalProvider = ({ children }) => {
    // Use a simple object instead of Map for easier serialization and React compatibility if needed.
    // Actually, the plan used Map, so I'll stick to new Map() as it triggers re-renders correctly when replaced.
    const [modalStates, setModalStates] = useState(new Map());
    const [areAllMinimized, setAreAllMinimized] = useState(false);

    const minimizeAll = useCallback(() => {
        setAreAllMinimized(true);
        setModalStates(prev => {
            const newStates = new Map(prev);
            // Iterate through all registered modals
            for (const [key, state] of newStates) {
                newStates.set(key, {
                    ...state,
                    minimized: true,
                    previousVisibility: state.visible
                });
            }
            return newStates;
        });
    }, []);

    const restoreAll = useCallback(() => {
        setAreAllMinimized(false);
        setModalStates(prev => {
            const newStates = new Map(prev);
            for (const [key, state] of newStates) {
                newStates.set(key, {
                    ...state,
                    minimized: false,
                    visible: state.previousVisibility || false
                });
            }
            return newStates;
        });
    }, []);

    const registerModal = useCallback((id, initialState = {}) => {
        setModalStates(prev => {
            if (prev.has(id)) return prev;
            const newStates = new Map(prev);
            newStates.set(id, {
                visible: false,
                minimized: false,
                ...initialState
            });
            return newStates;
        });
    }, []);

    const updateModal = useCallback((id, updates) => {
        setModalStates(prev => {
            const newStates = new Map(prev);
            const currentState = newStates.get(id) || { visible: false, minimized: false };
            newStates.set(id, { ...currentState, ...updates });
            return newStates;
        });
    }, []);

    const getModalState = useCallback((id) => {
        return modalStates.get(id) || { visible: false, minimized: false };
    }, [modalStates]);

    const value = {
        modalStates,
        areAllMinimized,
        minimizeAll,
        restoreAll,
        registerModal,
        updateModal,
        getModalState,
        MODAL_IDS
    };

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};
